import React, { useState, useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { validatePhoneNumber } from '../utils/phoneValidation';
import './Modal.css';
import './UserForm.css'
const apiUrl = process.env.REACT_APP_API_URL;

const BASE_URL = `${apiUrl}/business`;

// Default 12 PM opening to 11 PM closing in 30-min intervals if not configured
const DEFAULT_SLOTS = Array.from({ length: 22 }, (_, i) => {
  const totalMinutes = 12 * 60 + i * 30; // 12:00 to 22:30
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const id = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const displayHour = h % 12 || 12;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const title = `${displayHour}:${String(m).padStart(2, '0')} ${ampm}`;
  return { id, title, enabled: true };
});

const formatLocalDate = (dateObj) => {
  if (!dateObj) return '';
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const UserForm = ({ onSubmit, allentered_check, initialData = true, queue_info, auto_queue, usercheck, isPhoneRequired, business_id, tables=[], business_type="", isReservation=false, reservedQueue=null }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    country_code: '91',
    no_of_people: business_type === "other" ? 1 : '',
    queue_id: '',
    queue_type: usercheck ? '' : 'waiting',
    estimated_arrival_time: '',
    table_no: '',
    table_id: '',
    remarks: '',
    metadata: {},
  });
  const nameInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [selectedQueueType, setSelectedQueueType] = useState('waiting');
  // const [tables, setTables] = useState(get_tables);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reservationDays, setReservationDays] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [slotError, setSlotError] = useState('');

  useEffect(() => {
    if (isReservation) {
      setSelectedQueueType('reserved');
      const reservedId = reservedQueue?.queue_id || queue_info?.find(q => q.queue_type === 'reserved')?.queue_id || '';
      setFormData(prev => ({
        ...prev,
        queue_type: 'reserved',
        queue_id: reservedId || prev.queue_id,
        estimated_arrival_time: prev.estimated_arrival_time || ''
      }));
    }
  }, [isReservation, reservedQueue, queue_info]);

  // Initialize default selectedDate when queue_type is reserved
  useEffect(() => {
    if (selectedQueueType === 'reserved') {
      if (formData.estimated_arrival_time) {
        const d = new Date(formData.estimated_arrival_time * 1000);
        setSelectedDate(formatLocalDate(d));
      } else if (!selectedDate) {
        setSelectedDate(formatLocalDate(new Date()));
      }
    }
  }, [selectedQueueType, formData.estimated_arrival_time, selectedDate]);

  // Fetch available reservation days when reserved
  useEffect(() => {
    if ((isReservation || selectedQueueType === 'reserved') && business_id) {
      const fetchReservationDays = async () => {
        try {
          const response = await fetch(`${BASE_URL}/reservation_days?business_id=${business_id}`);
          const result = await response.json();
          if (result && result.data) {
            setReservationDays(result.data);
            if (result.data.min_date) {
              setSelectedDate((prev) => {
                if (!prev) return result.data.min_date;
                if (prev < result.data.min_date) return result.data.min_date;
                return prev;
              });
            }
          }
        } catch (error) {
          console.error('Error fetching reservation days:', error);
        }
      };

      fetchReservationDays();
    }
  }, [isReservation, selectedQueueType, business_id]);

  // Fetch available slots when selectedDate or business_id changes
  useEffect(() => {
    if ((isReservation || selectedQueueType === 'reserved') && business_id && selectedDate) {
      const fetchSlots = async () => {
        setSlotsLoading(true);
        try {
          const response = await fetch(`${BASE_URL}/reservation_slots?business_id=${business_id}&date=${selectedDate}`);
          const result = await response.json();
          if (result && result.data && Array.isArray(result.data) && result.data.length > 0) {
            setAvailableSlots(result.data);
          } else {
            setAvailableSlots(DEFAULT_SLOTS);
          }
        } catch (error) {
          console.error('Error fetching reservation slots:', error);
          setAvailableSlots(DEFAULT_SLOTS);
        } finally {
          setSlotsLoading(false);
        }
      };

      fetchSlots();
    }
  }, [isReservation, selectedQueueType, business_id, selectedDate]);

  // Sync selectedSlotId with formData.estimated_arrival_time
  useEffect(() => {
    if (formData.estimated_arrival_time && selectedDate) {
      const d = new Date(formData.estimated_arrival_time * 1000);
      const slotDateStr = formatLocalDate(d);
      if (slotDateStr === selectedDate) {
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        setSelectedSlotId(`${hh}:${mm}`);
      } else {
        setSelectedSlotId('');
      }
    }
  }, [formData.estimated_arrival_time, selectedDate]);

  const isSlotInPast = (slotId) => {
    if (!selectedDate) return false;
    const now = new Date();
    const todayStr = formatLocalDate(now);
    if (selectedDate !== todayStr) return false;

    const [hours, minutes] = slotId.split(':').map(Number);
    const slotTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);
    return slotTime <= now;
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlotId(slot.id);
    setSlotError('');
    const [hours, minutes] = slot.id.split(':');
    const [year, month, day] = (selectedDate || formatLocalDate(new Date())).split('-').map(Number);
    const targetDate = new Date(year, month - 1, day, parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    const timestamp = Math.floor(targetDate.getTime() / 1000);
    setFormData((prev) => ({
      ...prev,
      estimated_arrival_time: timestamp,
    }));
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setSelectedSlotId('');
    setSlotError('');
    setFormData((prev) => ({
      ...prev,
      estimated_arrival_time: '',
    }));
  };

  // const fetchTables = async (token) => {
  //   try {
  //     const response = await fetch(`${BASE_URL}/get_business_tables`, {
  //       method: 'GET',
  //       credentials: 'include',
  //     });
  //     const data = await response.json();
  //     setTables(data.data || []);
  //   } catch (error) {
  //     console.error('Error fetching tables:', error);
  //   }
  // };
  // useEffect(() => {
    
  //   fetchTables();
  // }, []);
  
  
  useEffect(() => {
    if (usercheck) {
      const savedFormData = {
        name: localStorage.getItem('name') || '',
        phone: localStorage.getItem('phone') || '',
        country_code: (localStorage.getItem('country_code') || '91').replace(/^\+/, ''),
        no_of_people: business_type === "other" ? 1 : (localStorage.getItem('no_of_people') || '')
      };
      setFormData((prevFormData) => ({
        ...prevFormData,
        ...savedFormData
      }));
    }
  }, [usercheck, business_type]);

  useEffect(() => {
    // Focus the Name input when the form loads
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);


  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
  };


  useEffect(() => {
    if (business_id === 10 || business_id === 11) {
      setFormData(prevFormData => ({
        ...prevFormData,
        no_of_people: 1
      }));
    }
  }, [business_id]);

  useEffect(() => {
    if (initialData) {
       // Check if the queue_type is 'reserved' and set the selected queue type
       if (initialData.queue_type === 'reserved') {
        setSelectedQueueType('reserved');
      }
      
      setFormData((prevFormData) => ({
        ...prevFormData,
        name: initialData.customer_name || prevFormData.name || initialData.name,
        phone: initialData.phone_number || prevFormData.phone || initialData.phone,
        country_code: (initialData.country_code ? String(initialData.country_code).replace(/^\+/, '') : (prevFormData.country_code || '91')),
        no_of_people: initialData.no_of_people || prevFormData.no_of_people,
        queue_type: initialData.queue_type || prevFormData.queue_type,
        table_no: initialData.table_no || prevFormData.table_no,
        estimated_arrival_time: initialData.estimated_arrival_time
          ? (new Date(initialData.estimated_arrival_time).getTime() / 1000) + 19800
          : (prevFormData.estimated_arrival_time 
            ? prevFormData.estimated_arrival_time
            : ''),
        remarks: (initialData.notes?.[0]?.note_text) || prevFormData.remarks || '',
        table_id: initialData.table_id || prevFormData.table_id,
        metadata: {
          ...prevFormData.metadata,
          next_queue_id: initialData.next_queue_id || prevFormData.metadata?.next_queue_id || ''
        },
        ...(initialData.queue_type === 'reserved' && {
          queue_id: initialData.queue_id || prevFormData.queue_id,
        }),
      }));
      
    }
  }, [initialData]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'metadata.next_queue_id') {
      const reservedId = reservedQueue?.queue_id || queue_info?.find(q => q.queue_type === 'reserved')?.queue_id;
      setFormData(prev => ({
        ...prev,
        queue_id: isReservation ? (reservedId || prev.queue_id) : value, // Update queue_id for UI reflection
        metadata: {
          ...prev.metadata,
          next_queue_id: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (['name', 'phone', 'no_of_people', 'table_no'].includes(name)) {
      localStorage.setItem(name, value);
    }

    if (name === 'queue_type') {
      setSelectedQueueType(value); 
      if (value === 'reserved') {
        const foundReserved = reservedQueue || queue_info?.find(queue => queue.queue_type === 'reserved');
        if (foundReserved) {
          setFormData(prevFormData => ({
            ...prevFormData,
            queue_id: foundReserved.queue_id
          }));
        }
      } else if (value === 'waiting') {
        setFormData(prevFormData => ({
          ...prevFormData,
          queue_id: ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (isPhoneRequired || (formData.phone && formData.phone.trim() !== '')) {
      const validation = validatePhoneNumber(formData.phone, formData.country_code, isPhoneRequired);
      if (!validation.isValid) {
        setPhoneError(validation.message);
        return;
      }
    }
    setPhoneError('');
    if (selectedQueueType === 'reserved') {
      if (!formData.estimated_arrival_time) {
        setSlotError('Please select a reservation time slot.');
        return;
      }
    }
    setSlotError('');
    setIsSubmitting(true);
  
    let dataToSubmit = {
      ...formData,
      country_code: (formData.country_code || '91').replace(/^\+/, ''),
      phone: formData.phone ? String(formData.phone).trim() : '',
    };
  
    // Remove unnecessary fields based on queue type
    if (usercheck) {
      delete dataToSubmit.table_no;
      delete dataToSubmit.table_id;
    }
  
    if (selectedQueueType === 'waiting') {
      delete dataToSubmit.estimated_arrival_time;
      delete dataToSubmit.remarks;
    }
  
    // Remove queue_id if it is null or an empty string
    if (!dataToSubmit.queue_id) {
      delete dataToSubmit.queue_id;
    }
    if (!dataToSubmit.table_id) {
      delete dataToSubmit.table_id;
    }
  
    try {
      if (allentered_check) {
        onSubmit(dataToSubmit); // Do not await
      } else {
        await onSubmit(dataToSubmit); // Await if allentered_check is false
      }
    } finally {
      setIsSubmitting(false); // Always reset isSubmitting regardless of the condition
    }    
  };
  

  return (
    <div>
    <form onSubmit={handleSubmit} className="user-form">
      <div className="form-group">
        <label>Name:</label>
        <input
          type="text"
          name="name"
          ref={nameInputRef} // Attach the ref to the Name input
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group phone-form-group">
        <label>Phone:</label>
        <PhoneInput
          country="in"
          preferredCountries={['in', 'us', 'gb', 'ae']}
          enableSearch={true}
          searchPlaceholder="Search country..."
          searchNotFound="No country found"
          value={formData.phone ? `${formData.country_code || '91'}${formData.phone}` : (formData.country_code ? `${formData.country_code}` : '')}
          onChange={(value, countryData) => {
            const dialCode = countryData?.dialCode ? String(countryData.dialCode) : '91';
            const nationalNumber = value.startsWith(dialCode) ? value.slice(dialCode.length) : value;
            setFormData(prev => ({
              ...prev,
              country_code: dialCode,
              phone: nationalNumber,
            }));
            if (usercheck) {
              localStorage.setItem('country_code', dialCode);
              localStorage.setItem('phone', nationalNumber);
            }
            if (phoneError) {
              setPhoneError('');
            }
          }}
          inputProps={{
            name: 'phone',
            required: isPhoneRequired,
            autoComplete: 'tel',
          }}
          specialLabel=""
        />
        {phoneError && <span className="phone-error-text">{phoneError}</span>}
      </div>

      {/* Only show No of People if business_type is not "Others" and business_id is not 10, 11, or 59 */}
      {business_type !== "other" && (business_id !== 10 && business_id !== 11 && business_id !== 59) && (
        <div className="form-group">
          <label>No of People:</label>
          <input
            type="number"
            name="no_of_people"
            value={formData.no_of_people}
            onChange={handleChange}
            required
          />
        </div>
      )}

      {/* Only show Table fields if business_type is not "Others" and not usercheck */}
      {business_type !== "other" && !usercheck && (
        <>
          {(tables ?? []).length > 0 ? (
            <div className="form-group">
              <label>Table:</label>
              <select
                name="table_id"
                value={formData.table_id}
                onChange={(e) => handleChange({
                  target: {
                    name: 'table_id',
                    value: e.target.value ? parseInt(e.target.value, 10) : null
                  }
                })}
              >
                <option value="">Select Table (Optional)</option>
                {tables.map((table) => (
                  <option key={table.id} value={table.id}>
                    {table.name} (Status: {table.status})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="form-group">
              <label>Table No:</label>
              <input
                type="text"
                name="table_no"
                value={formData.table_no}
                onChange={handleChange}
                placeholder="Enter Table No (Optional)"
              />
            </div>
          )}
        </>
      )}

      {/* Queue Type dropdown */}
      {!usercheck && (
        <div className="form-group">
          <label>Entry Type:</label>
          <select name="queue_type" value={formData.queue_type} onChange={handleChange} required>
            <option value="">Select Entry Type</option>
            <option value="waiting">Walk-in</option>
            <option value="reserved">Reservation</option>
          </select>
        </div>
      )}

      {((usercheck && !auto_queue) || (!usercheck && (selectedQueueType === 'waiting' || selectedQueueType === 'reserved') && !auto_queue)) && (
        <div className="form-group">
          <label>Category:</label>
          <select 
            name={selectedQueueType === 'reserved' ? 'metadata.next_queue_id' : 'queue_id'} 
            value={formData.queue_id} 
            onChange={handleChange} 
            required
          >
            <option value="">Select Category</option>
            {queue_info
              .filter((queue) => queue.queue_type !== 'reserved')
              .map((queue) => (
                <option key={queue.queue_id} value={queue.queue_id}>
                  {queue.queue_name}
                </option>
              ))}
          </select>
        </div>
      )}

      {/* Conditionally render fields if the queue type is 'reserved' */}
      {selectedQueueType === 'reserved' && (
        <>
          <div className="form-group">
            <label>Reservation Date:</label>
            <input
              type="date"
              name="estimated_arrival_date"
              min={reservationDays?.min_date || formatLocalDate(new Date())}
              max={reservationDays?.max_date || undefined}
              value={selectedDate}
              onChange={handleDateChange}
              required
            />
            {reservationDays?.unavailable_dates?.includes(selectedDate) && (
              <span className="reservation-notice-text">
                Note: Limited availability on this date. Reservation subject to confirmation.
              </span>
            )}
          </div>

          <div className="form-group">
            <label>Select Arrival Time Slot:</label>
            {slotsLoading ? (
              <div className="slots-loading-text">Loading available slots...</div>
            ) : (
              <div className="reservation-slots-grid">
                {(availableSlots.length > 0 ? availableSlots : DEFAULT_SLOTS).map((slot) => {
                  const past = isSlotInPast(slot.id);
                  const isAvailable = slot.enabled && !past;
                  const isSelected = selectedSlotId === slot.id;
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      className={`slot-chip ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                      onClick={() => handleSlotSelect(slot)}
                    >
                      {slot.title}
                    </button>
                  );
                })}
              </div>
            )}
            {slotError && <span className="phone-error-text">{slotError}</span>}
          </div>

          <div className="form-group">
            <label>Remarks:</label>
            <input
              type="text"
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
            />
          </div>
        </>
      )}

    {usercheck && (
      <div className="terms">
        <label className="terms-label">
          <span style={{ paddingBottom: '4px', color: 'darkslategray' }}>
         By clicking the button below, you agree to our {' '}
            <span className="terms-link" onClick={handleModalToggle}>
              terms and conditions
            </span>
          </span>
        </label>
      </div>
    )}

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </button>
    </form>

      {isModalOpen && (
        <div className="modal-overlay" onClick={handleModalToggle}>
          <div className="modal-content" style={{ maxWidth: '290px' }} onClick={(e) => e.stopPropagation()}>
            <h2>Terms and Conditions</h2>
            <p class="terms-content">
  By submitting your information in this form, you agree to the following terms:
  <br /><br />
  <strong>Use of Contact Information:</strong><br />
  Your contact details, including your phone number, may be used by us to send transactional updates via WhatsApp, such as order confirmations, status updates, or other related information.
  <br /><br />
  <strong>Customer Assistance:</strong><br />
  The restaurant may contact you in case you leave behind personal belongings or for other customer service purposes.
  <br /><br />
  <strong>Data Privacy:</strong><br />
  Your personal data will be stored securely and will not be shared with third parties without your consent, except as necessary to provide the requested services.
  <br /><br />
  <strong>Opt-Out Option:</strong><br />
  You may opt out of marketing or promotional communications at any time by contacting the restaurant directly.
  <br /><br />
  <strong>Acceptance of Terms:</strong><br />
  By submitting your information, you acknowledge that you have read, understood, and agreed to these terms and conditions.
</p>

            <button onClick={handleModalToggle}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserForm;
